import { useState, useMemo } from 'react';
import type { TestRunEvent } from '../types';
import { Icon } from './Icon';
import { listRuns, clearRuns, type RunRecord } from '../lib/runHistory';

interface Props {
  events: TestRunEvent[];
  visible: boolean;
  onClose: () => void;
  refreshKey?: number;
}

function formatTime(ts: number) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

function formatAgo(ts: number) {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function TestRunDrawer({ events, visible, onClose, refreshKey }: Props) {
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const runs: RunRecord[] = useMemo(() => listRuns(), [refreshKey, tab]);

  if (!visible) return null;
  return (
    <div className="test-drawer" role="dialog">
      <div className="test-drawer-head">
        <div className="test-tabs">
          <button
            className={`test-tab ${tab === 'current' ? 'on' : ''}`}
            onClick={() => setTab('current')}
          >
            <Icon name="play2" size={11} variant={tab === 'current' ? 'gold' : 'muted'} />
            Current
          </button>
          <button
            className={`test-tab ${tab === 'history' ? 'on' : ''}`}
            onClick={() => setTab('history')}
          >
            <Icon name="reload" size={11} variant={tab === 'history' ? 'gold' : 'muted'} />
            History
            {runs.length > 0 && <span className="test-tab-count">{runs.length}</span>}
          </button>
        </div>
        <button className="x" onClick={onClose} aria-label="Close">
          <Icon name="x" size={13} variant="muted" />
        </button>
      </div>

      {tab === 'current' && (
        <div className="test-drawer-events">
          {events.length === 0 ? (
            <div className="test-empty">
              <Icon name="play2" size={22} variant="muted" />
              <span>
                No events yet. Click <b style={{ color: 'var(--gold-light)' }}>Test run</b> in the toolbar to simulate the workflow against fake data.
              </span>
            </div>
          ) : (
            events.map(ev => (
              <div className="test-event" key={ev.id}>
                <time>{formatTime(ev.ts)}</time>
                <div>
                  <b>{ev.nodeLabel}</b>
                  {ev.message ? <span>{ev.message}</span> : null}
                </div>
                <span className={`pill ${ev.status}`}>{ev.status}</span>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="test-drawer-events history">
          {runs.length === 0 ? (
            <div className="test-empty">
              <Icon name="reload" size={22} variant="muted" />
              <span>No past runs yet. Hit Test run to start logging history.</span>
            </div>
          ) : (
            <>
              {runs.map(run => {
                const isOpen = expandedRun === run.id;
                return (
                  <div key={run.id} className={`run-record ${isOpen ? 'expanded' : ''}`}>
                    <div className="run-head" onClick={() => setExpandedRun(isOpen ? null : run.id)}>
                      <span className={`pill ${run.outcome === 'success' ? 'fired' : run.outcome === 'failed' ? 'failed' : 'waiting'}`}>
                        {run.outcome}
                      </span>
                      <div className="run-info">
                        <b>{run.workflowName}</b>
                        <span>
                          {run.nodesFired} blocks · {(run.durationMs / 1000).toFixed(1)}s · {formatAgo(run.startedAt)}
                        </span>
                      </div>
                      <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={12} variant="muted" />
                    </div>
                    {isOpen && (
                      <div className="run-events">
                        {run.events.map(ev => (
                          <div className="test-event" key={ev.id}>
                            <time>{formatTime(ev.ts)}</time>
                            <div>
                              <b>{ev.nodeLabel}</b>
                              {ev.message ? <span>{ev.message}</span> : null}
                            </div>
                            <span className={`pill ${ev.status}`}>{ev.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="run-clear-row">
                <button
                  className="run-clear"
                  onClick={() => {
                    if (window.confirm('Clear all run history? This cannot be undone.')) {
                      clearRuns();
                      setExpandedRun(null);
                    }
                  }}
                >
                  <Icon name="trash" size={11} variant="muted" />
                  Clear history
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
